import SentryService from 'app/vanilla/services/Sentry';

/**
 * Remote Router Runtime Plugin for Module Federation
 * -------------------------------------------------
 * This plugin extends the capabilities of the module federation runtime by customizing the behavior of the application at runtime.
 *
 * Functionality:
 * 1. The plugin provides several hooks to customize the behavior of the application at runtime.
 * 2. It handles the loading of remote microfrontends and provides a way to handle errors that may occur during this process.
 *
 * How it Works:
 * - The 'beforeRequest' method is called before a request is made to load a remote microfrontend. It modifies the entry property of the remotes to point to the correct URLs.
 * - The 'onLoad' method is called when a remote microfrontend is fully loaded. It records a Sentry breadcrumb with the module id.
 * - The 'errorLoadRemote' method is called when there's an error loading a remote microfrontend. It records a Sentry breadcrumb, reports the error to Sentry and returns nothing, so the module federation runtime rethrows the original error.
 *
 * Usage:
 * - The plugin is a crucial part of the host application as it handles the loading of remote microfrontends.
 *
 * Notes:
 * - This plugin provides a flexible way to control the loading of remote microfrontends, optimizing the loading process and handling errors efficiently.
 * - The Sentry hooks never throw: the module federation runtime emits them without error handling, so a failure in the instrumentation would break the loading of every remote.
 *
 * References:
 * - Module Federation discussion
 *   https://github.com/module-federation/universe/discussions/1936
 *
 * - Module Federation runtime documentation
 *   https://github.com/module-federation/universe/tree/main/packages/runtime
 *   https://github.com/module-federation/universe/tree/main/packages/enhanced
 */

window.MFE_REMOTE_HOST = window._PREO_DATA && window._PREO_DATA._COMPONENTS_URL;
window.MFE_REMOTE_HOST_DEFAULT = 'https://ui-qa.withqikserve.com/';

/*
 * Rewrite default remote entry urls for the `@online-ordering/*` MFEs.
 *
 * The MFE host url is configured as environment url (`window.MFE_REMOTE_HOST`).
 * If not provided, the url configured in Webpack Module Federation plugin will be used.
 */
function getRemoteEntryUrl(remoteEntry) {
  // Append a query param with a unique value to prevent caching
  const timestamp = Date.now();

  if (!window.MFE_REMOTE_HOST) {
    return `${remoteEntry}?t=${timestamp}`;
  }

  const remoteHost = window.MFE_REMOTE_HOST.replace(/\/$/, '');
  const remoteEntryUrl = new URL(remoteEntry);

  return `${remoteHost}${remoteEntryUrl.pathname}?t=${timestamp}`;
}

function getNetworkStatus() {
  return navigator.onLine ? 'ONLINE' : 'OFFLINE';
}

function captureBreadcrumb(message) {
  try {
    SentryService.captureMicroFrontendBreadcrumb(message);
  } catch {
    // Telemetry must never break the loading of a remote
  }
}

function captureException(error) {
  try {
    SentryService.captureException(error);
  } catch {
    // Telemetry must never break the loading of a remote
  }
}

export default function webOrdersRemoteRouter() {
  return {
    name: 'web-orders-remote-router',
    // Called before resolving the remote container, used to inject containers or update certain content before lookup
    // https://module-federation.io/plugin/dev/index.html#beforerequest
    beforeRequest(args) {
      args.options.remotes.forEach((remote) => {
        remote.entry = getRemoteEntryUrl(remote.entry);
      });

      return args;
    },
    // Triggered when the federated module is fully loaded, allowing access to and modification of the exported content of the loaded file
    // https://module-federation.io/plugin/dev/index.html#onload
    onLoad(args) {
      captureBreadcrumb(`The federated module "${args.id}" is fully loaded. The network status is "${getNetworkStatus()}".`);
      return args;
    },
    // This hook is invoked when the loading of a federated module fails, enabling customized error handling strategies
    // https://module-federation.io/plugin/dev/index.html#errorloadremote
    errorLoadRemote(args) {
      captureBreadcrumb(`Fail loading the federated module "${args.id}" from "${args.from}" on lifecycle "${args.lifecycle}". The network status is "${getNetworkStatus()}".`);
      captureException(args.error);
    },
  };
}
